<template>
  <nav
    :class="$style.nav"
    :style="{
      ...(isFirstScreen && !isPortraitMode
        ? { opacity: 0, pointerEvents: 'none' }
        : {}),
      ...(isFirstScreen && isPortraitMode && !isDrawerOpen
        ? { background: 'transparent', borderBottomColor: 'transparent' }
        : {}),
    }"
  >
    <!-- Logo -->
    <div
      v-if="logoImg"
      :class="$style.logo"
      :style="
        isFirstScreen && isPortraitMode && !isDrawerOpen
          ? { backgroundColor: 'transparent' }
          : null
      "
    >
      <img :src="logoImg" />
    </div>

    <!-- 横屏导航菜单 -->
    <ul v-if="!isPortraitMode" :class="$style['nav-menu']">
      <li
        v-for="(item, index) in navItems"
        :key="index"
        :class="$style['nav-menu-item']"
      >
        <button
          :class="[currentSection === item.section && $style.active]"
          @click="scrollToSection(item.section)"
        >
          {{ item.title }}
        </button>
      </li>
    </ul>

    <!-- 竖屏导航菜单切换按钮 -->
    <button
      v-if="isPortraitMode"
      :class="[
        $style['nav-drawer-toggle'],
        isDrawerOpen && $style.open,
        isFirstScreen && !isDrawerOpen && $style.white,
      ]"
      @click="toggleDrawer"
    >
      <span />
      <span />
      <span />
    </button>

    <!-- 竖屏导航菜单 -->
    <div
      v-if="isPortraitMode"
      :class="[$style['nav-drawer-wrapper'], isDrawerOpen && $style.open]"
    >
      <!-- 半透明遮罩层 -->
      <div :class="$style['nav-drawer-overlay']" @click="closeDrawer" />

      <!-- 菜单抽屉 -->
      <ul :class="$style['nav-drawer']">
        <li
          v-for="(item, index) in navItems"
          :key="index"
          :class="$style['nav-drawer-item']"
        >
          <button
            :class="[currentSection === item.section && $style.active]"
            @click="scrollToSection(item.section)"
          >
            {{ item.title }}
          </button>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'LandingNav',
  data() {
    return {
      // 是否为竖屏模式
      isPortraitMode: false,
      // 抽屉是否展开
      isDrawerOpen: false,
      currentSection: 1,
      // fullpage.js 实例引用
      fullpageInstance: null,
      // 导航菜单项配置
      navItems: [
        { section: 1, title: '首页' },
        { section: 2, title: '极致产品' },
        { section: 3, title: '关于极致' },
        { section: 4, title: '加入我们' },
        { section: 5, title: '联系我们' },
      ],
    }
  },
  computed: {
    ...mapGetters(['logo', 'logoHorizontalWhite', 'logoHorizontalWhiteRed']),
    logoImg() {
      // 竖屏首屏且菜单未打开时，使用白红logo
      if (this.isPortraitMode && this.isFirstScreen && !this.isDrawerOpen) {
        return this.logoHorizontalWhiteRed
      }
      // 竖屏其他情况使用纯白色logo
      if (this.isPortraitMode) {
        return this.logoHorizontalWhite
      }
      // 横屏使用默认logo
      return this.logo
    },
    // 判断是否是首屏
    isFirstScreen() {
      return this.currentSection === 1
    },
  },
  watch: {
    // 监听抽屉的展开/收起状态，控制fullpage滚动行为
    isDrawerOpen(isOpen) {
      if (!this.fullpageInstance) return

      if (isOpen) {
        // 菜单打开时禁用 fullpage 滚动
        this.fullpageInstance.setAllowScrolling(false)
        this.fullpageInstance.setKeyboardScrolling(false)
      } else {
        // 菜单关闭时恢复 fullpage 滚动
        this.fullpageInstance.setAllowScrolling(true)
        this.fullpageInstance.setKeyboardScrolling(true)
      }
    },
  },
  mounted() {
    this.updateCurrentSection()

    // 添加全局事件监听器作为备选方案
    this.$root.$on('screen-info-changed', this.handleScreenInfoChange)
  },
  beforeDestroy() {
    // 组件销毁时恢复 fullpage 滚动
    if (this.fullpageInstance) {
      this.fullpageInstance.setAllowScrolling(true)
      this.fullpageInstance.setKeyboardScrolling(true)
    }

    // 移除全局事件监听器
    this.$root.$off('screen-info-changed', this.handleScreenInfoChange)
  },
  methods: {
    // 处理屏幕方向变化的事件监听器
    handleScreenInfoChange(newScreenInfo) {
      if (!newScreenInfo) {
        return
      }

      // 根据屏幕方向切换显示模式
      this.isPortraitMode = newScreenInfo.type !== 'landscape'

      // 横竖屏切换时，如果抽屉处于展开状态则自动收起
      if (this.isDrawerOpen && !this.isPortraitMode) {
        this.closeDrawer()
      }
    },
    // 使用 fullpage.js API 滚动到指定屏幕索引
    scrollToSection(sectionNumber) {
      if (this.fullpageInstance) {
        this.fullpageInstance.moveTo(sectionNumber)
      } else {
        // 降级方案：fullpage.js 不可用时使用原生平滑滚动
        const sections = document.querySelectorAll('.section')
        if (sections[sectionNumber - 1]) {
          sections[sectionNumber - 1].scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      }

      // 点击菜单项后自动收起抽屉
      if (this.isDrawerOpen) {
        this.closeDrawer()
      }
    },
    // 更新当前所在屏幕索引（用于高亮当前菜单项）
    updateCurrentSection(sectionNumber = null) {
      if (sectionNumber !== null) {
        // 直接设置指定的屏幕索引
        this.currentSection = sectionNumber
      } else if (this.fullpageInstance) {
        // 从 fullpage.js 实例获取当前激活的屏幕索引
        const activeSection = this.fullpageInstance.getActiveSection()
        if (activeSection) {
          this.currentSection = activeSection.index + 1
        }
      }
    },
    // 设置 fullpage.js 实例引用（由父组件调用）
    setFullpageInstance(instance) {
      this.fullpageInstance = instance
    },
    // 切换抽屉的展开/收起状态
    toggleDrawer() {
      this.isDrawerOpen = !this.isDrawerOpen
    },
    // 收起抽屉
    closeDrawer() {
      this.isDrawerOpen = false
    },
  },
}
</script>

<style lang="less" module>
.nav {
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-bottom: 0.01rem solid #a1a1a1;
  transition: opacity 0.3s ease, background-color 0.3s ease,
    border-bottom-color 0.3s ease;
}

.logo {
  position: absolute;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: @landing-primary;

  img {
    object-fit: contain;
  }
}

.nav-menu {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;

  &-item {
    display: flex;
    align-items: center;
    margin-right: 1rem;

    & button {
      font-size: 0.28rem;
      color: #656567;
      vertical-align: top;
      white-space: nowrap;

      &.active {
        color: @landing-primary;
        font-weight: 700;
      }
    }
  }
}

.nav-drawer-toggle {
  .size(0.7rem, 0.7rem);

  position: absolute;
  top: 50%;
  right: 0.47rem;
  transform: translateY(-50%);
  flex-direction: column;
  justify-content: space-around;

  span {
    .size(100%, 0.1rem);

    display: block;
    background-color: #5f5e5e;
    transition: all 0.3s ease;
    transform-origin: right;
  }

  &.white span {
    background-color: #fff;
  }

  &.open span {
    &:nth-child(1) {
      transform: rotate(-42deg);
    }

    &:nth-child(2) {
      opacity: 0;
    }

    &:nth-child(3) {
      transform: rotate(42deg);
    }
  }
}

.nav-drawer-wrapper {
  position: fixed;
  top: 1.3rem;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 999;

  &.open {
    pointer-events: auto;
  }
}

.nav-drawer-wrapper.open .nav-drawer-overlay {
  opacity: 1;
  visibility: visible;
}

.nav-drawer-wrapper.open .nav-drawer {
  transform: translateX(0);
  opacity: 1;
  visibility: visible;
}

.nav-drawer-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 5.26rem;
  padding-top: 0.48rem;
  background-color: #fff;
  transform: translateX(100%);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  z-index: 1;

  &-item {
    width: 2.28rem;
    margin: 0 auto 0.62rem;
  }

  button {
    .size(100%, 0.74rem);

    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 0.08rem solid transparent;
    color: #5f5e5e;
    font-size: 0.42rem;
    font-weight: 700;

    &.active {
      color: @landing-primary;
      border-color: @landing-primary;
    }
  }
}

// 横屏
:global(.landing-screen-landscape) {
  .nav {
    height: 0.85rem;
  }

  .logo {
    left: 0.55rem;
    width: 1.15rem;

    img {
      .size(0.75rem);
    }
  }

  .nav-menu {
    display: flex;
  }

  .nav-drawer-toggle {
    display: none;
  }
}

// 竖屏
:global(.landing-screen-portrait) {
  .nav {
    height: 1.3rem;
  }

  .logo {
    left: 0;
    right: 0;
    width: 2.98rem;
    margin: 0 auto;

    img {
      .size(100%);
    }
  }

  .nav-menu {
    display: none;
  }

  .nav-drawer-toggle {
    display: flex;
  }
}

// 标准屏
:global(.landing-screen-standard) {
  .nav {
    width: 10.8rem;
    margin: 0 auto;
  }
}
</style>
